import { TFunction } from 'i18next';
import isNil from 'lodash/isNil';
import qs from 'qs';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { queryClient } from '~/client/queryClient';
import Button from '~/components/buttons/NewButton';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { RouteConfig } from '~/routes/routeConfig';
import { AnalyticsEvents } from '~/services/analytics';
import { invalidateConsentCaches, useConsentQuery } from '~/services/consents';
import { invalidateDocumentsAndFlowsCache } from '~/services/document';
import { useSibillSocket } from '~/state/notifications/socket';
import { AccountQueryKeys } from '~/types/account';
import { ConsentQueryKeys } from '~/types/consent';
import { DocumentStatus, DocumentQueryKeys } from '~/types/document';
import { FlowsQueryKeys } from '~/types/flow';
import { InstitutionType } from '~/types/institution';
import { InvoicesQueryKeys } from '~/types/invoicing';
import { PaymentStatus } from '~/types/payment';
import { F24QueryKeys, F24Status } from '~/types/taxes';
import { TransactionsKeys } from '~/types/transaction';
import { isSdiCoopInstitution } from '~/utils/institutions';
import { SibillSocketChannel } from '../sibill-socket';

interface CompanyChannelDeps {
  t: TFunction<'notifications', undefined>;
}

const companyChannelName = (companyId: string) => `company:${companyId}`;

interface ConsentSyncPayload {
  consent_id: string;
  type: InstitutionType;
  first_sync: boolean;
  documents: number | null;
  flows: number | null;
  accounts: number | null;
  transactions: number | null;
}

const ConsentAddedNotificationContent = ({
  payload,
  closeNotification,
}: {
  payload: ConsentSyncPayload;
  closeNotification: () => void;
}) => {
  const { t } = useTranslation('notifications');
  const { isLoading, data } = useConsentQuery(payload.consent_id);

  if (isLoading || isNil(data)) return null;
  return (
    <>
      <Text variant="body2" weight="regular">
        {payload.first_sync ? (
          <Trans
            t={t}
            i18nKey={'consent_first_notification_done.message'}
            components={{
              bold: <Text variant="body2" weight="semibold" />,
            }}
            values={{ accountName: data.institution?.name }}
          />
        ) : (
          <Trans
            t={t}
            i18nKey={'consent_notification_done.message'}
            components={{
              bold: <Text variant="body2" weight="semibold" />,
            }}
            context={data.purpose}
            values={{
              transactionNumber: payload.transactions,
              accountName: data.institution?.name,
            }}
          />
        )}
      </Text>
      <Button
        href={`${RouteConfig.Accounts.buildLink()}#${payload.consent_id}`}
        onClick={closeNotification}
        variant="link"
        style={{ whiteSpace: 'nowrap' }}
      >
        {t('consent_first_notification_done.link_text')}
      </Button>
    </>
  );
};

const ConsentUpdatedNotificationContent = ({ payload }: { payload: ConsentSyncPayload }) => {
  const { t } = useTranslation('notifications');
  const { isLoading, data } = useConsentQuery(payload.consent_id);

  if (isLoading || isNil(data)) return null;
  return (
    <Text variant="body2" weight="regular">
      <Trans
        t={t}
        i18nKey={'consent_updated_notification.message'}
        components={{
          bold: <Text variant="body2" weight="semibold" />,
        }}
        values={{ accountName: data.institution?.name }}
      />
    </Text>
  );
};

const consentUpdatedEvent = (payload: ConsentSyncPayload) => {
  invalidateConsentCaches();
  snack.enqueue(
    {
      message: () => <ConsentUpdatedNotificationContent payload={payload} />,
      type: 'success',
    },
    { timeout: 10000 },
  );
};

const consentSyncEvent = (payload: ConsentSyncPayload) => {
  const type = payload.type;
  invalidateConsentCaches();

  if (type === InstitutionType.Banking) {
    queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: TransactionsKeys.all });
    if (payload.first_sync || (payload.transactions ?? 0) > 0) {
      const snackbarKey = snack.enqueue(
        {
          message: () => (
            <ConsentAddedNotificationContent closeNotification={() => snack.close(snackbarKey)} payload={payload} />
          ),
          type: 'success',
        },
        { timeout: 10000 },
      );
    }
  }
  if (type === InstitutionType.Accounting) {
    queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: FlowsQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.all });
    if (payload.first_sync || (payload.documents ?? 0) > 0) {
      const snackbarKey = snack.enqueue(
        {
          message: () => (
            <IntegrationAddedNotificationContent payload={payload} closeNotification={() => snack.close(snackbarKey)} />
          ),
          type: 'success',
        },
        { timeout: 10000 },
      );
    }
  }
};

const AccountAddedNotificationContent = ({
  payload,
  closeNotification,
}: {
  payload: ConsentSyncPayload;
  closeNotification: () => void;
}) => {
  const { t } = useTranslation('notifications');
  const { isLoading, data } = useConsentQuery(payload.consent_id);
  if (isLoading || isNil(data)) return null;
  return (
    <>
      <Text variant="body2" weight="regular">
        {payload.first_sync
          ? t('account_first_notification_done.message', {
              accountName: data.institution?.name,
            })
          : t('account_notification_done.message', {
              accountName: data.institution?.name,
              transactionNumber: payload.transactions,
            })}
        <Button
          href={`${RouteConfig.Accounts.buildLink()}#${payload.consent_id}`}
          onClick={closeNotification}
          variant="link"
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('account_first_notification_done.link_text')}
        </Button>
      </Text>
    </>
  );
};

const accountSyncEvent = (payload: ConsentSyncPayload) => {
  const type = payload.type;
  invalidateConsentCaches();
  queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });

  if (type === InstitutionType.Banking) {
    if (payload.first_sync || (payload.transactions ?? 0) > 0) {
      const snackbarKey = snack.enqueue(
        {
          message: () => (
            <AccountAddedNotificationContent closeNotification={() => snack.close(snackbarKey)} payload={payload} />
          ),
          type: 'success',
        },
        { timeout: 10000 },
      );
    }
  }
};

const IntegrationAddedNotificationContent = ({
  payload,
  closeNotification,
}: {
  payload: ConsentSyncPayload;
  closeNotification: () => void;
}) => {
  const { t } = useTranslation('notifications');
  const { isLoading, data } = useConsentQuery(payload.consent_id);

  if (isLoading || isNil(data) || isNil(data.institution)) return null;
  const showLink = !isSdiCoopInstitution(data.institution);

  return (
    <div>
      <Text variant="body2" weight="regular">
        {payload.first_sync
          ? t('integration_first_notification_done.message', {
              integrationName: data.institution.name,
            })
          : t('integration_notification_done.message', {
              integrationName: data.institution.name,
            })}
        {showLink ? (
          <Button
            href={RouteConfig.Invoices.buildLink()}
            variant="link"
            onClick={closeNotification}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('integration_first_notification_done.link_text')}
          </Button>
        ) : null}
      </Text>
    </div>
  );
};

interface PaymentStatusChangedPayload {
  payment_id: string;
  status: PaymentStatus;
}

const paymentStatusChanged = (payload: PaymentStatusChangedPayload) => {
  invalidateDocumentsAndFlowsCache();

  // eslint-disable-next-line no-console
  console.log('PAYMENT_STATUS_CHANGED', payload);
};

interface InvoiceDeliveryErrorPayload {
  document_id: string;
  number: number;
  counterpart_name: string;
  status: DocumentStatus;
}

const invoiceDeliveryErrored = (payload: InvoiceDeliveryErrorPayload, { t }: CompanyChannelDeps) => {
  const message: string =
    payload.status === DocumentStatus.Refused
      ? t('invoice_refused_notification_done.message', {
          invoiceNumber: payload.number,
          counterpartName: payload.counterpart_name,
        })
      : t('invoice_discarded_notification_done.message', {
          invoiceNumber: payload.number,
          counterpartName: payload.counterpart_name,
        });

  const search = qs.stringify(
    { id: payload.document_id, search: encodeURIComponent(payload.number), status: ['DISCARDED', 'REFUSED'] },
    { arrayFormat: 'repeat' },
  );

  snack.enqueue(
    {
      message,
      type: 'error',
      link: {
        href: `${RouteConfig.Invoices.buildLink()}/issued?${search}`,
        label: t('invoice_refused_notification_done.link_text'),
      },
    },
    { timeout: 10000 },
  );
};

interface PayrollBookkeepingProposalsReadyPayload {
  document_id: string;
  sender: string;
}

const PayrollBookkeepingProposalsNotificationContent = ({
  documentId,
  sender,
  closeNotification,
}: {
  documentId: string;
  sender: string;
  closeNotification: () => void;
}) => {
  const { t } = useTranslation('notifications');
  return (
    <>
      <Text variant="body2" weight="regular">
        {t('payroll_bookkeeping_proposals_ready_notification.message', { sender })}
      </Text>
      <Button
        href={RouteConfig.BookkeepingPayrollRegistration.buildLink({ documentId })}
        onClick={closeNotification}
        variant="link"
        style={{ whiteSpace: 'nowrap' }}
      >
        {t('payroll_bookkeeping_proposals_ready_notification.link_text')}
      </Button>
    </>
  );
};

const payrollBookkeepingProposalsReady = (payload: PayrollBookkeepingProposalsReadyPayload) => {
  // Newly generated payroll proposals surface as documents in the non-electronic
  // documents table (useDocumentInfiniteQuery), so refresh the documents cache.
  queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });

  // Persistent toast (no timeout): it stays until the user clicks through to the
  // registration, at which point the link handler dismisses it.
  const snackbarKey = snack.enqueue({
    message: () => (
      <PayrollBookkeepingProposalsNotificationContent
        documentId={payload.document_id}
        sender={payload.sender}
        closeNotification={() => snack.close(snackbarKey)}
      />
    ),
    type: 'info',
  });
};

interface BackendMessagePayload {
  message: string;
}

const backendMessage = (payload: BackendMessagePayload) => {
  snack.enqueue(
    {
      message: payload.message,
      type: 'error',
    },
    { timeout: 10000 },
  );
};

interface F24PaidPayload {
  f24_upload_id: string;
  f24_id: string;
  status: F24Status;
}

const F24PaidNotificationContent = ({ f24Id, closeNotification }: { f24Id: string; closeNotification: () => void }) => {
  const { t } = useTranslation('notifications');
  const { track } = useAnalytics();

  return (
    <>
      <Text variant="body2" weight="regular">
        {t('f24_paid_notification.message')}
      </Text>
      <Button
        href={RouteConfig.F24.buildLink()}
        onClick={() => {
          track(AnalyticsEvents.F24_RECEIPT_DOWNLOAD_CLICKED, { f24Id, fromNotification: true });
          closeNotification();
        }}
        variant="link"
        style={{ whiteSpace: 'nowrap' }}
      >
        {t('f24_paid_notification.link_text')}
      </Button>
    </>
  );
};

const f24Paid = (payload: F24PaidPayload) => {
  queryClient.invalidateQueries({ queryKey: F24QueryKeys.all });

  const snackbarKey = snack.enqueue(
    {
      message: () => (
        <F24PaidNotificationContent f24Id={payload.f24_id} closeNotification={() => snack.close(snackbarKey)} />
      ),
      type: 'success',
    },
    { timeout: 10000 },
  );
};

const registerCompanyChannelListeners = (channel: SibillSocketChannel, deps: CompanyChannelDeps) => {
  channel.registerEvent('CONSENT_UPDATED', (payload: ConsentSyncPayload) => consentUpdatedEvent(payload));
  channel.registerEvent('CONSENT_SYNC_DONE', (payload: ConsentSyncPayload) => consentSyncEvent(payload));
  channel.registerEvent('ACCOUNT_SYNC_DONE', (payload: ConsentSyncPayload) => accountSyncEvent(payload));
  channel.registerEvent('PAYMENT_STATUS_CHANGED', (payload: PaymentStatusChangedPayload) =>
    paymentStatusChanged(payload),
  );
  channel.registerEvent('INVOICE_DELIVERY_ERROR', (payload: InvoiceDeliveryErrorPayload) =>
    invoiceDeliveryErrored(payload, deps),
  );
  channel.registerEvent('PAYROLL_BOOKKEEPING_PROPOSALS_READY', (payload: PayrollBookkeepingProposalsReadyPayload) =>
    payrollBookkeepingProposalsReady(payload),
  );
  channel.registerEvent('F24_PAID', (payload: F24PaidPayload) => f24Paid(payload));
  channel.registerEvent('BACKEND_MESSAGE', (payload: BackendMessagePayload) => backendMessage(payload));
};

export const useCompanySocketConnection = (changeConnectionStatus: boolean) => {
  const companyChannel = useRef<SibillSocketChannel | undefined>(undefined);
  const companyId = useActiveCompanyId();
  const { joinChannel } = useSibillSocket();
  const { t } = useTranslation('notifications');

  useEffect(() => {
    if (!companyId) return;

    companyChannel.current = joinChannel(companyChannelName(companyId));
    if (!companyChannel.current) return;

    registerCompanyChannelListeners(companyChannel.current, { t });

    return () => companyChannel.current?.leave();
  }, [companyId, joinChannel, changeConnectionStatus, t]);

  return companyChannel;
};
